<div class="flag-details-panel">
  <!-- ── Top meta row ─────────────────────────────────── -->
  <div class="meta-row" id="tour-flag-details-meta">
    <div class="meta-badge entity-badge">
      <span class="meta-badge-label">Entity</span>
      <span class="meta-badge-value">{{ report.entity_type | titlecase }}</span>
    </div>

    @if (report.severity_label) {
      <div class="meta-badge" [ngClass]="getSeverityClass(report.severity)">
        <span class="meta-badge-label">Severity</span>
        <span class="meta-badge-value">{{ report.severity_label }}</span>
      </div>
    }

    @if (!isEditMode && report.status) {
      <div class="meta-badge" [ngClass]="getStatusClass(report.status)">
        <span class="meta-badge-label">Status</span>
        <span class="meta-badge-value">{{
          getStatusLabel(report.status)
        }}</span>
      </div>
    }

    <span class="meta-date">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      {{ report.created_at | date: "mediumDate" }}
    </span>
  </div>

  <!-- ── Detail cards ─────────────────────────────────── -->
  <div class="details-grid">
    <!-- Reporter Details -->
    @if (report.reporter) {
      <div class="detail-card">
        <div class="detail-card-label">Reported By</div>
        <div class="detail-card-value">
          {{ getReporterFullName(report.reporter) || "—" }}
        </div>
      </div>

      <div class="detail-card">
        <div class="detail-card-label">Reporter Email</div>
        <div class="detail-card-value">
          {{ report.reporter.email || "—" }}
        </div>
      </div>
    }

    <!-- Reason – full width -->
    <div class="detail-card detail-card-full" id="tour-flag-details-reason">
      <div class="detail-card-label">Reason</div>
      <div class="detail-card-value">{{ report.reason_label }}</div>
    </div>

    <!-- Details text – full width -->
    <div class="detail-card detail-card-full">
      <div class="detail-card-label">Details</div>
      @if (report.details) {
        <div class="detail-text-block">{{ report.details }}</div>
      } @else {
        <span class="empty-value">No details provided.</span>
      }
    </div>

    <!-- Attached Files – full width -->
    <div class="detail-card detail-card-full" id="tour-flag-details-files">
      <div class="detail-card-label">
        Attached Files
        @if (report.files.length) {
          <span class="files-count-badge">{{ report.files.length }}</span>
        }
      </div>

      @if (report.files.length) {
        <div class="files-list">
          @for (file of report.files; track file.file_id) {
            <div
              class="file-item"
              tabindex="0"
              role="button"
              (click)="$event.stopPropagation(); openFile.emit(file)"
              (keydown.enter)="$event.stopPropagation(); openFile.emit(file)"
              (keydown.space)="
                $event.stopPropagation();
                openFile.emit(file);
                $event.preventDefault()
              "
            >
              <div class="file-item-info">
                <div class="file-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <span class="file-name" [title]="file.file_name">{{
                  file.file_name
                }}</span>
              </div>

              <button
                type="button"
                class="download-btn"
                title="Download file"
                (click)="$event.stopPropagation(); downloadFile.emit(file)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 3v12" />
                  <path d="m7 10 5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
              </button>
            </div>
          }
        </div>
      } @else {
        <span class="empty-value">No files attached.</span>
      }
    </div>

    <!-- Reported Entity Details Card (Manage Flags only) -->
    @if (
      isManageFlags &&
      (entityDetails ||
        report.entity_type === entityType.BUSINESS ||
        report.entity_type === entityType.EMAIL)
    ) {
      <div
        class="detail-card detail-card-full entity-details-card"
        id="tour-flag-details-entity"
      >
        <div class="detail-card-label">{{ entityDetailsTitle }}</div>

        <div class="entity-details-grid">
          @if (entityDetails) {
            <!-- User -->
            @if (
              entityDetails.user && report.entity_type !== entityType.BUSINESS
            ) {
              <div class="entity-detail-item">
                <span class="entity-item-label">User</span>
                <span class="entity-item-value">
                  {{ getUserFullName(entityDetails.user) }}
                </span>
              </div>
            }

            <!-- Connection -->
            @if (
              entityDetails.connection &&
              report.entity_type !== entityType.BUSINESS
            ) {
              <div class="entity-detail-item">
                <span class="entity-item-label">Connection</span>
                <span class="entity-item-value">
                  {{
                    entityDetails.connection.display_name ||
                      entityDetails.connection.connection_name
                  }}
                </span>
              </div>
            }

            <!-- File Entity -->
            @if (
              report.entity_type === entityType.FILE && entityDetails.details
            ) {
              <div class="entity-detail-item entity-item-full">
                <span class="entity-item-label">File</span>
                <div class="file-item">
                  <div
                    class="file-item-info"
                    tabindex="0"
                    role="button"
                    (click)="$event.stopPropagation(); openEntityFile.emit()"
                    (keydown.enter)="
                      $event.stopPropagation(); openEntityFile.emit()
                    "
                    (keydown.space)="
                      $event.stopPropagation();
                      openEntityFile.emit();
                      $event.preventDefault()
                    "
                  >
                    <div class="file-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                    <span
                      class="file-name"
                      [title]="entityDetails.details['file_name']"
                    >
                      {{ entityDetails.details["file_name"] }}
                    </span>
                  </div>

                  <button
                    type="button"
                    class="download-btn"
                    title="Download file"
                    (click)="
                      $event.stopPropagation(); downloadEntityFile.emit()
                    "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M12 3v12" />
                      <path d="m7 10 5 5 5-5" />
                      <path d="M5 21h14" />
                    </svg>
                  </button>
                </div>
              </div>
            }

            <!-- Note Entity -->
            @if (
              report.entity_type === entityType.NOTE && entityDetails.details
            ) {
              <div class="entity-detail-item entity-item-full">
                <span class="entity-item-label">Note</span>
                <div
                  class="note-content-block"
                  [innerHTML]="entityDetails.details['note']"
                ></div>
                <div class="entity-item-footer">
                  <span class="entity-item-date">
                    {{
                      entityDetails.details["updated_on_utc"] | date: "medium"
                    }}
                  </span>
                  <button
                    type="button"
                    class="view-record-link"
                    (click)="viewRecord.emit()"
                  >
                    View Record
                  </button>
                </div>
              </div>
            }

            <!-- Notification Entity -->
            @if (
              report.entity_type === entityType.NOTIFICATION &&
              entityDetails.details
            ) {
              <div class="entity-detail-item entity-item-full">
                <span class="entity-item-label">Notification</span>
                <div class="notification-card">
                  <div
                    class="notification-title"
                    [innerHTML]="entityDetails.details['title']"
                  ></div>
                  <div class="notification-body">
                    {{ entityDetails.details["body"] }}
                  </div>
                  <span class="notification-time">
                    {{ entityDetails.details["created_on_utc"] | timeAgo }}
                  </span>
                </div>
              </div>
            }

            @if (
              report.entity_type === entityType.EMAIL && entityDetails.details
            ) {
              <div class="entity-detail-item entity-item-full email-details">
                <span class="entity-item-label">Email</span>
                @if (entityDetails.details["subject"]) {
                  <div class="email-subject">
                    {{ entityDetails.details["subject"] }}
                  </div>
                }
                @if (entityDetails.details["body"]) {
                  <div
                    class="email-body"
                    [innerHTML]="entityDetails.details['body']"
                  ></div>
                }
              </div>
            }
            @if (
              !entityDetails.user &&
              !entityDetails.connection &&
              !entityDetails.details?.["subject"] &&
              !entityDetails.details?.["body"]
            ) {
              <span class="empty-value">No email details available.</span>
            }
          }

          <!-- Business Entity -->
          @if (report.entity_type === entityType.BUSINESS) {
            <div class="entity-detail-item entity-item-full">
              <button
                type="button"
                class="view-record-btn"
                (click)="viewRecord.emit()"
              >
                View Business Record
              </button>
            </div>
          }
        </div>
      </div>
    }

    <!-- Status Edit Card (when isEditMode) -->
    @if (isEditMode) {
      <div class="detail-card detail-card-full edit-card">
        <label class="detail-card-label" for="edit-flag-status">Status</label>
        <select
          id="edit-flag-status"
          class="edit-select"
          [(ngModel)]="editForm.status"
        >
          @for (status of editableStatusOptions; track status.value) {
            <option [ngValue]="status.value">{{ status.label }}</option>
          }
        </select>
      </div>
    }

    <!-- Resolution Notes – full width (moved below reported entity details) -->
    <div
      class="detail-card detail-card-full"
      id="tour-flag-details-resolution"
      [class.edit-card]="isEditMode"
    >
      <label class="detail-card-label" for="edit-resolution-notes"
        >Resolution Notes</label
      >
      @if (isEditMode) {
        <textarea
          id="edit-resolution-notes"
          class="edit-textarea"
          [(ngModel)]="editForm.resolution_notes"
          rows="4"
          placeholder="Add resolution notes..."
        ></textarea>
      } @else if (report.resolution_notes) {
        <div class="detail-text-block">{{ report.resolution_notes }}</div>
      } @else {
        <span class="empty-value">No resolution notes provided.</span>
      }
    </div>
  </div>
</div>
